import {
  Card,
  CardContent,
  Typography,
  Divider,
  Box,
  Stack,
  Chip,
  alpha,
  useTheme,
  Button,
  Avatar,
} from "@mui/material";
import React from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { getTodaysBirthday } from "../../api/services/notification";
import CakeIcon from "@mui/icons-material/Cake";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";

const Birthdays = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery("birthdays", getTodaysBirthday, {
    enabled: true,
    keepPreviousData: true,
  });

  const birthdayList = data?.data || [];

  const handleViewAll = () => {
    navigate("/operations?tab=alerts");
  };

  return (
    <Card
      sx={{
        height: 320,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CakeIcon sx={{ color: "#f57c00", fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
              Today's Birthdays
            </Typography>
            {birthdayList.length > 0 && (
              <Chip
                label={birthdayList.length}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  bgcolor: alpha("#f57c00", 0.08),
                  color: "#f57c00",
                }}
              />
            )}
          </Box>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            onClick={handleViewAll}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.7rem",
              color: theme.palette.primary.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            View All
          </Button>
        </Box>
      </CardContent>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : birthdayList.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1,
            }}
          >
            <EmojiEmotionsIcon sx={{ fontSize: 40, color: alpha(theme.palette.grey[400], 0.5) }} />
            <Typography variant="body2" color="text.secondary" align="center">
              No birthdays today
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              Check back tomorrow! 🎂
            </Typography>
          </Box>
        ) : (
          birthdayList.map((person, index) => (
            <Box key={person.id}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1.5,
                  px: 1,
                  borderRadius: 1.5,
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                  },
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: alpha("#f57c00", 0.12),
                      color: "#f57c00",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    {person.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" fontWeight={500} color="#1A1A2E">
                    {person.name}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <CalendarTodayIcon sx={{ color: "#0288d1", fontSize: 14 }} />
                    <Typography variant="caption" color="text.secondary">
                      {person.birthdate}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <AccessTimeIcon sx={{ color: "#43a047", fontSize: 14 }} />
                    <Typography variant="caption" color="text.secondary">
                      {person.age} years
                    </Typography>
                  </Stack>

                  <Chip
                    icon={<CakeIcon sx={{ fontSize: 12 }} />}
                    label="Celebrating"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.6rem",
                      fontWeight: 600,
                      bgcolor: alpha("#f57c00", 0.08),
                      color: "#f57c00",
                    }}
                  />
                </Box>
              </Box>

              {index < birthdayList.length - 1 && (
                <Divider sx={{ opacity: 0.4 }} />
              )}
            </Box>
          ))
        )}
      </Box>
    </Card>
  );
};

export default Birthdays;